import { type SubmitEvent, useState } from "react";

import { type AnyApiError, ApiError, isSuccess } from "@/api/ApiResult";
import { useCrud } from "@/api/useCrud";
import { Alert } from "@/components/ui/Alert/Alert";
import { Button } from "@/components/ui/Button/Button";
import { Form } from "@/components/ui/Form/Form";
import { FormLayout } from "@/components/ui/Form/FormLayout";
import { InputField } from "@/components/ui/InputField/InputField";
import { t } from "@/i18n/translate";
import type {
  CREATE_FIRST_ADMIN_REQUEST_PATH,
  CreateFirstAdminRequest,
  LoginResponse,
} from "@/types/generated/openapi";
import { StringFormData } from "@/utils/stringFormData";

import { type UserValidationErrors, validateCreateUser } from "@/utils/validateUserAccount";

interface FormError {
  title: string;
  message?: string;
}

interface CreateFirstAdminFormProps {
  next: () => void;
}

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: TODO function should be refactored
export function CreateFirstAdminForm({ next }: CreateFirstAdminFormProps) {
  const [validationErrors, setValidationErrors] = useState<UserValidationErrors | null>(null);
  const createPath: CREATE_FIRST_ADMIN_REQUEST_PATH = "/api/initialise/first-admin";
  const { create, isLoading } = useCrud<LoginResponse>({ createPath });
  const [formError, setFormError] = useState<FormError | null>(null);
  const [apiError, setApiError] = useState<AnyApiError | null>(null);

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();

    const formData = new StringFormData(event.currentTarget);

    const account: CreateFirstAdminRequest = {
      username: formData.getString("username"),
      fullname: formData.getString("fullname"),
      temp_password: formData.getString("password"),
    };

    const passwordRepeat = formData.getString("password_repeat");
    const errors = validateCreateUser(account, passwordRepeat);

    if (Object.keys(errors).length > 0) {
      setFormError({ title: t("account.not_saved"), message: t("account.check_fields") });
      setValidationErrors(errors);
      return;
    }

    setFormError(null);
    setValidationErrors(null);

    void create(account).then((result) => {
      if (isSuccess(result)) {
        next();
        return;
      }

      if (result instanceof ApiError) {
        switch (result.reference) {
          case "InvalidFullname":
            setFormError({
              title: t("error.api_error.InvalidFullname"),
              message: t("account.invalid_fullname"),
            });
            setValidationErrors({ fullname: t("account.invalid_fullname") });
            return;
          case "InvalidUsername":
            setFormError({
              title: t("error.api_error.InvalidUsername"),
              message: t("account.invalid_username"),
            });
            setValidationErrors({ username: t("account.invalid_username") });
            return;
          case "PasswordRejectionSameAsOld":
          case "PasswordRejectionSameAsUsername":
          case "PasswordRejectionTooShort":
            setFormError({ title: t("account.not_saved"), message: t("account.check_fields") });
            setValidationErrors({ password: t(`error.api_error.${result.reference}`) });
            return;
          default:
            setApiError(result);
            return;
        }
      }
    });
  }

  return (
    <>
      <header>
        <section>
          <h1>{t("initialise.create_admin_account")}</h1>
        </section>
      </header>
      <main>
        <article className="no_footer">
          {formError && (
            <FormLayout.Alert>
              <Alert type="error">
                <strong className="heading-md">{formError.title}</strong>
                <p>{formError.message}</p>
              </Alert>
            </FormLayout.Alert>
          )}

          {apiError instanceof ApiError && (
            <FormLayout.Alert>
              <Alert type="error">
                <p>{t(`error.api_error.${apiError.reference}`)}</p>
              </Alert>
            </FormLayout.Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <FormLayout disabled={isLoading}>
              <FormLayout.Section title={t("initialise.account_details")}>
                <p>{t("initialise.store_in_safe_place")}</p>
                <InputField
                  id="fullname"
                  name="fullname"
                  label={t("account.fullname")}
                  subtext={t("account.fullname_subtext")}
                  hint={t("initialise.fullname_hint")}
                  autoComplete="name"
                  maxLength={255}
                  error={validationErrors?.fullname}
                />
                <InputField
                  id="username"
                  name="username"
                  label={t("initialise.username")}
                  hint={t("initialise.username_hint")}
                  autoComplete="username"
                  maxLength={255}
                  error={validationErrors?.username}
                />
                <InputField
                  id="password"
                  name="password"
                  label={t("initialise.password")}
                  hint={t("initialise.password_hint")}
                  type="password"
                  autoComplete="new-password"
                  error={validationErrors?.password}
                />
                <InputField
                  id="password_repeat"
                  name="password_repeat"
                  label={t("account.password_repeat")}
                  type="password"
                  autoComplete="new-password"
                  error={validationErrors?.password_repeat}
                />
              </FormLayout.Section>
              <FormLayout.Controls>
                <Button type="submit">{t("save")}</Button>
              </FormLayout.Controls>
            </FormLayout>
          </Form>
        </article>
      </main>
    </>
  );
}
